/* Who may enter pages 2 and 3, decided as a function rather than an expression
   buried in a component.

   THE DEFECT THIS EXISTS TO PREVENT
   ---------------------------------
   The first version of this gate lived inline in NewAuditPage and read:

       userPlan === 'PRO' || userPlan === 'MAX' || !!paymentSessionId

   which is correct-looking and, for a pay-per-audit customer, could never open.
   `paymentSessionId` is set when payment COMPLETES, and payment happens at step
   4 (Confirm). The URL fields are at step 2. So the one group the extra pages
   are sold to was asked for its URLs before the thing that would unlock them,
   and the fields were permanently disabled with an "Upgrade" message.

   A gate that reads correctly and can never open is the same shape as a test
   that passes and asserts nothing: the failure is invisible at the point where
   someone would look for it.

   It went unnoticed because it was untestable where it sat. This project has no
   component-rendering tests and no @testing-library dependency, so the only
   available check was a source grep for the constant — which the broken version
   passed. Extracted here, every case is a plain assertion.

   AND THE SECOND VERSION WAS ALSO WRONG
   -------------------------------------
   The fix added `paymentRequired`, which is set when the free allowance is
   EXHAUSTED — not when a customer wants to pay. So a customer who had not yet
   spent their free audit still could not buy three pages: they had to burn the
   free one on a single page first, and only then did the fields open. That is
   not choose-then-run, it is choose-after-spending-something-else, and it made
   the upgrade path for this feature invisible to everyone who had not already
   hit the wall.

   THE RULE, FINALLY: choose-then-run, for everyone.
   Anyone may ENTER pages 2 and 3. Doing so makes the audit a purchase, and
   Confirm routes to checkout — free allowance or not. A free allowance buys a
   ONE-PAGE audit; it was never a discount on a three-page one.

   Both defects have the same root: the field's availability was derived from
   payment state. Entering a page and paying for it are different acts, and
   they now have different functions.

   THIS IS THE VISIBLE HALF ONLY. The server decides independently in
   backend/audit/page_set.py on the real is_paid, so a client that lies about
   its plan is still refused. Nothing here grants an entitlement; it decides
   whether to show fields that the server may still reject. */

/* CommonJS on purpose. Every test in this project is plain `require` under a
   jest with no babel transform, and webpack resolves named imports from a CJS
   module without complaint. Writing this as ESM would mean either a new babel
   config or a test that cannot load the thing it tests — and an untestable
   gate is exactly what produced the defect above. */

/** Plans whose subscription already includes multi-page.
 *
 * EMPTY, AND THAT IS THE CURRENT TRUTH. It held ['PRO', 'MAX'] until
 * 2026-08-31. Those tiers stopped being purchasable on 2026-07-16 and the list
 * was kept "because those accounts exist" — measured on production, they do
 * not: users by plan is FREE 84, STARTER 5, and zero accounts sit outside
 * those two. The list was granting an entitlement to nobody while giving any
 * future plan string a place to arrive already entitled.
 *
 * A plan-based bypass returns here only when a plan that really includes
 * multi-page exists AND has been created in both gateways. Until then the only
 * thing that covers extra pages is a resolved payment. */
const MULTI_PAGE_PLANS = [];

/**
 * Whether the extra URL fields should be ENTERABLE.
 *
 * Everyone, whenever the feature is on. That is the whole rule now, and the
 * simplicity is the point — see the note above about what the previous two
 * versions of this got wrong.
 *
 * Entering a page is not the same act as paying for it. Deciding one by the
 * other is what produced both defects: a customer who wants three pages has to
 * be able to SAY so before anything can charge them for it.
 */
function canEnterExtraPages({ featureEnabled = true } = {}) {
  return !!featureEnabled;
}

/**
 * Whether this page set costs money the customer has not yet committed.
 *
 * The paywall moved here from the input. A free allowance buys a ONE-PAGE
 * audit; a second or third page is a purchase, whether or not the free audit
 * has been used. So the trigger is "did they ask for more than one page and are
 * they not already entitled", never "have they run out of free audits".
 *
 * @param {object}  state
 * @param {string}  state.userPlan          from GET /auth/me -> plan
 * @param {?string} state.paymentSessionId  set once a per-audit payment resolves
 * @param {number}  state.extraPageCount    how many of pages 2 and 3 are filled
 */
function multiPageRequiresPayment({
  userPlan,
  paymentSessionId,
  extraPageCount = 0,
} = {}) {
  if (extraPageCount <= 0) return false;      // one page: the free tier covers it
  if (paymentSessionId) return false;         // already paid for this audit
  if (MULTI_PAGE_PLANS.includes(userPlan)) return false;
  return true;
}

/* CommonJS on purpose. Every test in this project is plain `require` under a
   jest with no babel transform, and webpack resolves named imports from a CJS
   module without complaint. Writing this as ESM would mean either a new babel
   config or a test that cannot load the thing it tests — and an untestable
   gate is exactly what produced the defects above. */
module.exports = {
  canEnterExtraPages,
  multiPageRequiresPayment,
  MULTI_PAGE_PLANS,
};
module.exports.default = canEnterExtraPages;
